/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";
import ApiError from "./components/Alert/ApiError";
import { Breadcrumb, Footer } from "./parts";
import { Forbidden } from "./components/Forbidden";
import { LoginContainer } from "./pages/login/LoginContainer";
import { SignUpRouter } from "./pages/signup";
import RegisterComplete from "./pages/signup/RegisterComplete";
import ForgotPassword from "./pages/login/ForgotPassword";
import SecurityGuard from "./components/SecurityGuard";
import { useSelector } from "react-redux";
import ResetPassword from "./pages/login/ResetPassword";
import { UserSettingRouter } from "./pages/userSetting";
import { UserRouter } from "./pages/admin/user";
import { StudyRouter } from "./pages/study";
import { CustomerPACSRouter } from "./pages/customerPACS";
import UserEmailValidationEmail from "./components/UserEmailValidationEmail/UserEmailValidationEmail";
import { RoleRouter } from "./pages/admin/user/roles";
import SettingRoute from "./pages/admin/setting";
import { IRootState } from "./config/reducers";
import { LayoutAction } from "./reducers/slices/layoutSlice";
import { useAppDispatch } from "./config/store";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { TermAndServicePage } from "@src/components/TermsAndService/TermAndServicePage";
import { checkAcceptEula } from "@src/reducers/operations/eulaOperation";
import { useBrand } from "@src/pages/login/BrandContext";
import { CriticalResultRouter } from "./pages/criticalResult";
import { BreadcrumbState } from "./parts/Breadcrumb";
import { faro } from "@grafana/faro-web-sdk";
import { NetworkRequestErrorAlert } from "@advapacs/common";

const combinedRoutes = [UserSettingRouter, RoleRouter];
const secondLeftSidebarRoutes = [StudyRouter];
const secondRightSidebarRoutes = [
  UserRouter,
  CustomerPACSRouter,
  CriticalResultRouter,
];
// @ts-ignore
const generateRoute = (routes) => {
  return (
    <>
      {routes.map((route: any) => {
        return (
          <Route key={route.path} path={route.path} element={route.element} />
        );
      })}
    </>
  );
};

const LeftLayout = (): JSX.Element => {
  return <Outlet />;
};

const RightLayout = (): JSX.Element => {
  return <Outlet />;
};
const WrapperBreadcrumb = (): JSX.Element => {
  const { replaceBreadscrumbState } = useSelector(
    (state: IRootState) => state.layoutSlice
  );
  return <Breadcrumb state={replaceBreadscrumbState as BreadcrumbState} />;
};
const MainLayout = (): JSX.Element => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(LayoutAction.setReplaceBreadscrumbState(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="content-wrapper" id="main-content">
      <WrapperBreadcrumb />
      <div className="content">
        <NetworkRequestErrorAlert />
        <ApiError key={pathname} />
        <Outlet />
      </div>
      <Footer />
    </div>
  );
};

const AppRoutes = (): JSX.Element => {
  const renderRoutes = generateRoute(combinedRoutes);
  const leftSidebarLayoutRoutes = generateRoute(secondLeftSidebarRoutes);
  const rightSidebarLayoutRoutes = generateRoute(secondRightSidebarRoutes);
  const { trackPageView } = useMatomo();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, pathname, sessionHasBeenFetched, account } =
    useSelector((state: IRootState) => state.loginReducer);
  const { acceptEula, eula } = useSelector(
    (state: IRootState) => state.eulaReducer
  );
  useEffect(() => {
    if (pathname !== "/" && isAuthenticated && sessionHasBeenFetched) {
      navigate(pathname);
      localStorage.removeItem("path");
    }
  }, [navigate, pathname, isAuthenticated, sessionHasBeenFetched]);

  useEffect(() => {
    if (isAuthenticated && window.location.pathname === "/SSO/SAML2") {
      navigate("/");
    }
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    if (sessionHasBeenFetched && isAuthenticated) {
      trackPageView({
        customDimensions: [
          {
            id: 1,
            value: account?.tenantId as string,
          },
          {
            id: 2,
            value: account?.id as string,
          },
        ],
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionHasBeenFetched, isAuthenticated, account]);

  // ============= FARO CURRENT CONTEXT =================
  useEffect(() => {
    if (isAuthenticated && account && sessionHasBeenFetched) {
      // check domain
      // tracking with user id and customer id and tenant id for CP.
      faro.api?.setUser({
        id: account?.id,
        attributes: {
          customerId: account?.customerId ?? "",
          region: localStorage.getItem("region") || "global",
          tenantId: account?.tenantId,
        },
      });
    } else {
      faro.api?.resetUser();
    }
  }, [isAuthenticated, account, sessionHasBeenFetched]);

  // FARO Checking router changes.
  useEffect(() => {
    // tracking router with faro.
    faro.api?.setView({ name: pathname });
  }, [pathname]);

  // check terms and services.
  useEffect(() => {
    const shouldCheckAcceptTermsAndServiceWhenAuthenticated =
      isAuthenticated && !acceptEula && !eula;
    if (shouldCheckAcceptTermsAndServiceWhenAuthenticated) {
      dispatch(checkAcceptEula());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, acceptEula, eula]);

  useEffect(() => {
    const shouldNavigateTermAndServiceWhenAuthenticated =
      isAuthenticated && !acceptEula && eula;
    if (shouldNavigateTermAndServiceWhenAuthenticated) {
      navigate("/terms-of-service");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, acceptEula, eula]);

  return (
    <SecurityGuard>
      <Routes>
        <Route path="*" element={<Navigate to="/" />} />
        <Route path="/Forbidden" element={<Forbidden />} />
        <Route path="/" element={<Navigate to="/Studies" />} />
        <Route path="/terms-of-service" element={<TermAndServicePage />} />
        <Route element={<MainLayout />}>{renderRoutes}</Route>
        <Route element={<LeftLayout />}>{leftSidebarLayoutRoutes}</Route>
        <Route element={<RightLayout />}>{rightSidebarLayoutRoutes}</Route>
        <Route path={SettingRoute.path} element={SettingRoute.component} />
      </Routes>
    </SecurityGuard>
  );
};

const NoHeaderLoginLayout = (): JSX.Element => {
  return <Outlet />;
};

const UnAuthenticationLayout = (): JSX.Element => {
  return (
    <div className="content-wrapper un-authentication">
      <ApiError />
      <div className={`content p-none m-none`}>
        <NetworkRequestErrorAlert />
        <Outlet />
      </div>
    </div>
  );
};

const LoginRoutes = (): JSX.Element => {
  const { isAuthenticated, pathname } = useSelector(
    (state: IRootState) => state.loginReducer
  );
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { pathname: pathRoutes } = useLocation();

  const { trackPageView } = useMatomo();

  useEffect(() => {
    trackPageView({
      customDimensions: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathRoutes]);

  useEffect(() => {
    if (isAuthenticated && pathname === "/SSO/SAML2") {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isAuthenticated]);

  return (
    <Routes>
      <Route element={<NoHeaderLoginLayout />}>
        <Route element={<RegisterComplete />} path="/RegisterComplete" />
        <Route
          element={<UserEmailValidationEmail />}
          path="/Auth/EmailValidation"
        />
      </Route>
      <Route path="/public/terms-of-service" element={<TermAndServicePage />} />
      <Route element={<UnAuthenticationLayout />}>
        <Route element={<LoginContainer />} path="/" />
        <Route element={<LoginContainer />} path="/Login" />
        <Route element={<LoginContainer />} path="/SSO/SAML2" />
        <Route element={<ForgotPassword />} path="/ForgotPassword" />
        <Route element={<ResetPassword />} path="/ResetPassword" />
        <Route element={<SignUpRouter />} path="/Signup/*" />
        {brand && brand.brandPath !== "OAuthCallback" && (
          <Route
            element={<LoginContainer />}
            path={`/brand/${brand.brandPath}`}
          />
        )}
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export { AppRoutes, LoginRoutes };
